/**
 * FormWebhooksService
 * Outgoing webhook subscriptions + delivery log.
 * Route: forms/api/form-webhooks
 */
import { BaseService } from '../base/BaseService';
import { unwrapFormsResponse as unwrap } from './unwrapFormsResponse';
/** Admin webhook service (JWT). */
export class FormWebhooksService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FormWebhooksService.DEFAULT_SERVICE_PATH,
            basePath: FormWebhooksService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** GET api/form-webhooks?formId= (omit formId for every subscription the caller can see). */
    async list(formId) {
        return (await this.listRaw(formId)).data ?? [];
    }
    listRaw(formId) {
        return this.http.get(this.buildUrlWithParams(undefined, { formId }));
    }
    /** POST api/form-webhooks — the secret is returned once. */
    async create(request) {
        return unwrap(await this.createRaw(request), 'Failed to create webhook');
    }
    createRaw(request) {
        return this.http.post(this.buildUrl(), request);
    }
    /** PUT api/form-webhooks/{id} */
    async update(id, request) {
        return unwrap(await this.updateRaw(id, request), `Failed to update webhook ${id}`);
    }
    updateRaw(id, request) {
        return this.http.put(this.buildUrl(String(id)), request);
    }
    /** DELETE api/form-webhooks/{id} */
    async remove(id) {
        const response = await this.removeRaw(id);
        return response.data ?? response.success;
    }
    removeRaw(id) {
        return this.http.delete(this.buildUrl(String(id)));
    }
    /** POST api/form-webhooks/{id}/rotate-secret — the new secret is returned once. */
    async rotateSecret(id) {
        return unwrap(await this.rotateSecretRaw(id), `Failed to rotate secret for webhook ${id}`);
    }
    rotateSecretRaw(id) {
        return this.http.post(this.buildUrl(`${id}/rotate-secret`), {});
    }
    /** POST api/form-webhooks/{id}/test — sends a signed test event. */
    async test(id) {
        return unwrap(await this.testRaw(id), `Failed to test webhook ${id}`);
    }
    testRaw(id) {
        return this.http.post(this.buildUrl(`${id}/test`), {});
    }
    /** GET api/form-webhooks/{id}/deliveries?page=&pageSize=&statusId= */
    async getDeliveries(id, query = {}) {
        return unwrap(await this.getDeliveriesRaw(id, query), `Deliveries for webhook ${id} not found`);
    }
    getDeliveriesRaw(id, query = {}) {
        return this.http.get(this.buildUrlWithParams(`${id}/deliveries`, {
            page: query.page,
            pageSize: query.pageSize,
            statusId: query.statusId,
        }));
    }
    /** POST api/form-webhooks/deliveries/{deliveryId}/redeliver */
    async redeliver(deliveryId) {
        return unwrap(await this.redeliverRaw(deliveryId), `Failed to redeliver ${deliveryId}`);
    }
    redeliverRaw(deliveryId) {
        return this.http.post(this.buildUrl(`deliveries/${deliveryId}/redeliver`), {});
    }
}
FormWebhooksService.DEFAULT_SERVICE_PATH = 'forms';
FormWebhooksService.BASE_PATH = 'api/form-webhooks';
//# sourceMappingURL=FormWebhooksService.js.map